(() => {
  customElements.whenDefined('product-quick-add').then(() => {
    if (customElements.get('dn-product-quick-add')) return;
    customElements.define('dn-product-quick-add', class extends customElements.get('product-quick-add') {
      connectedCallback() {
        this.mobile = window.matchMedia('(max-width: 699px)');
        this.refreshVisibility = () => {
          if (!this._scopeFrom || !this._scopeTo) return;
          this._onObserved([this._scopeFrom, this._scopeTo].map(target => {
            const bounds = target.getBoundingClientRect();
            return { target, boundingClientRect: bounds, isIntersecting: bounds.bottom > 0 && bounds.top < window.innerHeight };
          }));
        };
        super.connectedCallback();
        this.mobile.addEventListener('change', this.refreshVisibility);
        this.refreshVisibility();
      }
      _onObserved(entries) {
        super._onObserved(entries);
        if (!this.mobile?.matches || !this._scopeFrom || !this._scopeTo) return;
        const form = this._scopeFrom.getBoundingClientRect();
        const footer = this._scopeTo.getBoundingClientRect();
        const formVisible = form.bottom > 0 && form.top < window.innerHeight;
        const footerVisible = footer.bottom > 0 && footer.top < window.innerHeight;
        this.classList.toggle('is-visible', !formVisible && !footerVisible);
      }
      disconnectedCallback() {
        super.disconnectedCallback();
        this.mobile?.removeEventListener('change', this.refreshVisibility);
      }
    });
  });

  function estimateDelivery(price, country, method, discount, rates) {
    const subtotal = discount ? Math.round(price * 0.85) : price;
    const isDom = ['GP', 'MQ', 'RE', 'GF', 'YT', 'BL', 'MF', 'PM'].includes(country);
    let shipping = null;
    if (country === 'FR') shipping = method === 'relay' ? rates.relay : subtotal >= rates.threshold ? 0 : rates.fr;
    else if (isDom) shipping = subtotal >= rates.threshold ? 0 : rates.dom;
    else if (['BE', 'CH'].includes(country)) shipping = rates.eu;
    return { subtotal, shipping, total: shipping === null ? null : subtotal + shipping, gap: country === 'FR' || isDom ? Math.max(0, rates.threshold - subtotal) : 0 };
  }

  if (!customElements.get('dn-delivery-estimate')) {
    customElements.define('dn-delivery-estimate', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        this.copy = JSON.parse(this.querySelector('[data-dn-delivery-copy]').textContent);
        this.country = this.querySelector('[data-dn-country]');
        this.method = this.querySelector('[data-dn-method]');
        this.discount = this.querySelector('[data-dn-discount]');
        this.details = this.querySelector('details');
        let saved = {};
        try { saved = JSON.parse(sessionStorage.getItem('dn-delivery-choice') || '{}'); } catch {}
        const country = saved.country || this.dataset.country;
        this.country.value = [...this.country.options].some(option => option.value === country) ? country : 'OTHER';
        this.method.value = saved.method === 'relay' ? 'relay' : 'standard';
        this.discount.checked = saved.discount === true;
        this.details.open = saved.open === true;
        this.format = new Intl.NumberFormat(this.dataset.locale || 'fr-FR', { style: 'currency', currency: 'EUR' });
        this.addEventListener('change', () => { this.update(); this.save(); }, { signal: this.controller.signal });
        this.details.addEventListener('toggle', () => this.save(), { signal: this.controller.signal });
        this.update();
        this.hidden = false;
      }
      update() {
        const rates = Object.fromEntries(['threshold', 'fr', 'dom', 'relay', 'eu'].map(key => [key, Number(this.dataset[key])]));
        const result = estimateDelivery(Number(this.dataset.price), this.country.value, this.method.value, this.discount.checked, rates);
        const money = value => this.format.format(value / 100);
        this.querySelector('[data-dn-method-label]').hidden = this.country.value !== 'FR';
        this.querySelector('[data-dn-subtotal-label]').textContent = this.discount.checked ? this.copy.discounted : this.copy.product;
        this.querySelector('[data-dn-subtotal]').textContent = money(result.subtotal);
        this.querySelector('[data-dn-shipping]').textContent = result.shipping === null ? this.copy.unknown : result.shipping === 0 ? this.copy.free : money(result.shipping);
        this.querySelector('[data-dn-total]').textContent = result.total === null ? this.copy.unknown : money(result.total);
        this.querySelector('[data-dn-threshold-note]').textContent = result.gap ? this.copy.gap.replace('[amount]', money(result.gap)) : this.country.value === 'CH' || ['GP', 'MQ', 'RE', 'GF', 'YT', 'BL', 'MF', 'PM'].includes(this.country.value) ? this.copy.taxes : '';
      }
      save() {
        try { sessionStorage.setItem('dn-delivery-choice', JSON.stringify({ country: this.country.value, method: this.method.value, discount: this.discount.checked, open: this.details.open })); } catch {}
      }
      disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    });
  }

  if (!customElements.get('dn-ritual-video')) {
    customElements.define('dn-ritual-video', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.video = this.querySelector('video');
        this.button = this.querySelector('[data-dn-play]');
        this.chapters = [...this.querySelectorAll('[data-dn-chapter]')];
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.button.hidden = false;
        const refresh = () => {
          this.button.textContent = this.video.ended ? this.button.dataset.replayLabel : this.video.paused ? this.button.dataset.playLabel : this.button.dataset.pauseLabel;
          const active = this.chapters.reduce((last, chapter, index) => this.video.currentTime >= Number(chapter.dataset.dnChapter) ? index : last, -1);
          this.chapters.forEach((chapter, index) => {
            chapter.classList.toggle('is-current', index === active);
            if (index === active) chapter.setAttribute('aria-current', 'step');
            else chapter.removeAttribute('aria-current');
          });
          const progress = this.video.duration ? this.video.currentTime / this.video.duration : 0;
          this.style.setProperty('--dn-video-progress', Math.min(1, Math.max(0, progress)));
        };
        this.button.addEventListener('click', () => {
          this.autoplayAttempted = true;
          if (this.video.paused || this.video.ended) this.video.play().catch(refresh);
          else this.video.pause();
        }, { signal });
        this.chapters.forEach(chapter => chapter.addEventListener('click', () => {
          this.autoplayAttempted = true;
          const seek = () => { this.video.currentTime = Number(chapter.dataset.dnChapter); refresh(); };
          if (this.video.readyState >= 1) seek();
          else this.video.addEventListener('loadedmetadata', seek, { once: true, signal });
          this.video.play().catch(refresh);
        }, { signal }));
        ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata'].forEach(event => this.video.addEventListener(event, refresh, { signal }));
        this.video.addEventListener('play', () => { this.autoplayAttempted = true; }, { signal });
        this.reduced.addEventListener('change', () => { if (this.reduced.matches) this.video.pause(); }, { signal });
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.video.pause(); }, { signal });
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) this.video.pause();
            else if (entry.intersectionRatio >= 0.35 && !this.autoplayAttempted && this.dataset.autoplay === 'true' && !this.reduced.matches && !navigator.connection?.saveData) {
              this.autoplayAttempted = true;
              this.video.muted = true;
              this.video.play().catch(refresh);
            }
          });
        }, { threshold: [0, 0.35] });
        this.observer.observe(this.video);
        refresh();
      }
      disconnectedCallback() {
        this.video?.pause();
        this.observer?.disconnect();
        this.controller?.abort();
        this.controller = null;
      }
    });
  }
})();

